import { v4 as uuidv4 } from "uuid";

import { UserType } from "../interfaces/User";

import type { IStudio } from "../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import { TemplateType } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Template.interface";

import { IBackgroundType } from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Theme.interface";

import {
  EStudioElementType,
  type IStudioElement,
} from "../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Elements.interface";

/**
 * Escenas pre-armadas (Coming Soon / BRB / Ending) que se siembran en el primer
 * arranque según el perfil. Seed LOCAL: no depende de que el backend
 * /scene-templates esté poblado (el store backend sigue disponible vía
 * ModalSceneTemplate para diseños más ricos).
 *
 * Cada escena es un IStudio literal nuevo (NO se spreadea FullTemplate, que es
 * un singleton compartido que los adapters mutan) con: un área full-screen sin
 * media, un fondo de color (theme) y texto centrado como headline + subtítulo.
 * El texto usa fontFamily "Roboto" para no romper el pipeline de Pixi (que
 * crashea con fontFamily undefined). Bounds en porcentaje, fontSize en px de
 * design-space (1920x1080) — el TextWidget lo escala.
 */

interface ScenePresetSpec {
  title: string;
  subtitle: string;
  accent: string;
}

const BACKGROUND_COLOR = "#0b0b12";
const SUBTITLE_COLOR = "#9aa0b5";

const ACCENT_COMING_SOON = "#3b82f6";
const ACCENT_BRB = "#f59e0b";
const ACCENT_ENDING = "#a855f7";

const headlineElement = (text: string, accent: string): IStudioElement => ({
  id: `el-${uuidv4()}`,
  label: text,
  bounds: { left: 5, top: 36, width: 90, height: 18, zIndex: 10 },
  active: true,
  type: EStudioElementType.TEXT,
  options: {
    text,
    fontFamily: "Roboto",
    fontSize: "120",
    fontColor: accent,
    bold: true,
    italic: false,
    textAlign: "center",
  },
});

const subtitleElement = (text: string): IStudioElement => ({
  id: `el-${uuidv4()}`,
  label: text,
  bounds: { left: 10, top: 56, width: 80, height: 8, zIndex: 10 },
  active: true,
  type: EStudioElementType.TEXT,
  options: {
    text,
    fontFamily: "Roboto",
    fontSize: "44",
    fontColor: SUBTITLE_COLOR,
    bold: false,
    italic: false,
    textAlign: "center",
  },
});

const makeScene = (spec: ScenePresetSpec): IStudio => ({
  version: "0.1.0",
  id: uuidv4(),
  type: TemplateType.One,
  output: {
    quality: { audio: 1, video: 1 },
    sizes: { height: 1080, width: 1920 },
  },
  theme: {
    background: { type: IBackgroundType.color, color: BACKGROUND_COLOR },
  },
  areas: [
    {
      id: `area-${uuidv4()}`,
      label: "background",
      bounds: { width: 100, height: 100, top: 0, left: 0, zIndex: 1 },
      media: {
        bounds: { width: 100, height: 100, top: 0, left: 0 },
        resource: null,
      },
    },
  ],
  elements: [
    headlineElement(spec.title, spec.accent),
    subtitleElement(spec.subtitle),
  ],
  widgets: [],
});

const presetsFromSpecs = (specs: ScenePresetSpec[]): IStudio[] =>
  specs.map(makeScene);

/**
 * Devuelve las escenas pre-armadas para un perfil (sin contar la escena viva,
 * que la genera el adapter). Vacío para Grabación Local (captura sin audiencia
 * en vivo → no necesita Coming Soon/BRB/Ending).
 */
export const getScenePresets = (userType: UserType): IStudio[] => {
  switch (userType) {
    case UserType.Streamer:
    case UserType.Gamer:
      return presetsFromSpecs([
        {
          title: "Comenzamos en breve",
          subtitle: "Gracias por la espera",
          accent: ACCENT_COMING_SOON,
        },
        { title: "Ya volvemos", subtitle: "No te vayas", accent: ACCENT_BRB },
        {
          title: "¡Gracias por ver!",
          subtitle: "Nos vemos en el próximo stream",
          accent: ACCENT_ENDING,
        },
      ]);

    case UserType.Radio:
      return presetsFromSpecs([
        {
          title: "Al aire en breve",
          subtitle: "Subí el volumen",
          accent: ACCENT_COMING_SOON,
        },
        {
          title: "Volvemos enseguida",
          subtitle: "Seguinos en la sintonía",
          accent: ACCENT_BRB,
        },
        {
          title: "Gracias por escuchar",
          subtitle: "Hasta la próxima",
          accent: ACCENT_ENDING,
        },
      ]);

    case UserType.LiveConcerts:
      return presetsFromSpecs([
        {
          title: "El show comienza pronto",
          subtitle: "Preparate",
          accent: ACCENT_COMING_SOON,
        },
        {
          title: "Intervalo",
          subtitle: "Ya volvemos con más",
          accent: ACCENT_BRB,
        },
        {
          title: "¡Gracias por venir!",
          subtitle: "Hasta el próximo show",
          accent: ACCENT_ENDING,
        },
      ]);

    case UserType.VirtualClasses:
      return presetsFromSpecs([
        {
          title: "La clase comienza pronto",
          subtitle: "Acomodate",
          accent: ACCENT_COMING_SOON,
        },
        {
          title: "Recreo",
          subtitle: "Volvemos en unos minutos",
          accent: ACCENT_BRB,
        },
        {
          title: "Clase finalizada",
          subtitle: "Gracias por participar",
          accent: ACCENT_ENDING,
        },
      ]);

    case UserType.LocalRecording:
    default:
      return [];
  }
};
